/*
 * promo-now-price.js
 *
 * Patches Blockshop theme's <product-price-element> to handle the Clearance/NOW
 * pricing treatment when a customer changes variants on the PDP.
 *
 * Why this exists:
 * - Blockshop's <product-price-element>.updatePrices(variant) updates the
 *   native .product-price--compare and .product-price--original spans.
 *   It has no knowledge of our .promo-now markup, so without a patch:
 *     - Switching from variant A (eligible) to variant B (eligible, different
 *       price): only the native price spans update. Our .promo-now block goes
 *       stale, showing variant A's prices.
 *     - Switching from eligible to ineligible: our .promo-now stays rendered.
 *     - Switching from ineligible to eligible: native spans update, but our
 *       .promo-now block isn't created.
 *   This patch handles all four transition paths cleanly.
 *
 * How it works:
 * - Once theme.js has registered <product-price-element>, we patch its
 *   prototype.updatePrices method. For non-promo products, we delegate to
 *   the original implementation unchanged. For promo products, we run our
 *   own logic that mirrors what the snippet does server-side.
 * - The host element gets a 'has-promo-now' class when promo markup should
 *   be visible. CSS in promo-now.css uses this class to hide native price
 *   markup, so we don't have to manipulate aria-hidden on those spans.
 *
 * Reuse for cart recommendations:
 * - This file also exposes window.PromoNow.buildMarkup(...) so that
 *   assets/cart-recommendations-slider.js (file 11) can render the same
 *   .promo-now block consistently when it builds product cards in JS.
 */

(function() {
  'use strict';

  // ----------------------------------------------------------------------
  // Configuration
  // ----------------------------------------------------------------------

  var TARGET_ELEMENT = 'product-price-element';
  var PROMO_TAG = 'promo-now';
  var GIFT_CARD_TAG = 'gift-card';
  var GIFT_CARD_TYPE = 'Gift Cards';
  var HOST_CLASS = 'has-promo-now';
  var PERCENT_SENTINEL = '__PERCENT__';

  // Retry: theme.js loads via <script type="module"> which is deferred by
  // default. We're loaded via <script defer> which runs after document parse
  // but before DOMContentLoaded, in document order with other defer scripts.
  // theme.js (module) and our defer scripts both run before DOMContentLoaded,
  // but module scripts can be slightly later. The retry covers any timing gap.
  var RETRY_INTERVAL_MS = 100;
  var RETRY_MAX_ATTEMPTS = 50; // 5 seconds total

  // ----------------------------------------------------------------------
  // Money formatting
  // Mirrors theme.js ProductPrice.formatMoney exactly. Theme passes a
  // money_format string with a {{ amount }} placeholder. Variant prices
  // come in as integer cents.
  // ----------------------------------------------------------------------

  /**
   * Format a price (in cents) using the theme's money format string.
   * @param {number} cents - Price in cents (integer)
   * @param {string} moneyFormat - The shop money format (e.g. "${{amount}}")
   * @returns {string} Formatted price string
   */
  function formatMoney(cents, moneyFormat) {
    if (!moneyFormat) {
      return String(cents / 100);
    }
    var decimals = moneyFormat.indexOf('no_decimals') !== -1 ? 0 : 2;
    var locale = moneyFormat.indexOf('comma') !== -1 ? 'de-DE' : 'en-US';
    var formatted = new Intl.NumberFormat(locale, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    }).format(cents / 100);
    return moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/, formatted);
  }

  // ----------------------------------------------------------------------
  // Eligibility logic
  // Mirrors snippets/promo-now.liquid's eligibility chain:
  //   1. product.tags contains 'promo-now'
  //   2. NOT a gift card (type or tag)
  //   3. variant.available
  //   4. variant.compare_at_price > variant.price AND variant.price > 0
  // ----------------------------------------------------------------------

  /**
   * Parse a comma-separated tag list into an array of trimmed tags.
   * @param {string} tagsStr
   * @returns {string[]}
   */
  function parseTags(tagsStr) {
    if (!tagsStr) return [];
    var parts = tagsStr.split(',');
    var out = [];
    for (var i = 0; i < parts.length; i++) {
      var t = parts[i].trim();
      if (t) out.push(t);
    }
    return out;
  }

  /**
   * Determine if a variant on a tagged product is eligible for the NOW
   * treatment.
   * @param {Object} ctx - Product-level context
   * @param {string[]} ctx.tags - Product tags
   * @param {string} ctx.productType - Product type
   * @param {Object} variant - Shopify variant object
   * @returns {boolean}
   */
  function isEligible(ctx, variant) {
    if (!ctx || !variant) return false;
    if (!variant.id) return false;
    if (ctx.tags.indexOf(PROMO_TAG) === -1) return false;
    if (ctx.productType === GIFT_CARD_TYPE) return false;
    if (ctx.tags.indexOf(GIFT_CARD_TAG) !== -1) return false;
    if (!variant.available) return false;
    if (typeof variant.compare_at_price !== 'number') return false;
    if (typeof variant.price !== 'number') return false;
    if (variant.price <= 0) return false;
    if (variant.compare_at_price <= variant.price) return false;
    return true;
  }

  /**
   * Calculate the percent-off integer (matches Liquid's truncating
   * integer division: price_diff | times: 100 | divided_by: compare).
   * @param {number} priceCents
   * @param {number} compareCents
   * @returns {number}
   */
  function calcPercentOff(priceCents, compareCents) {
    var diff = compareCents - priceCents;
    return Math.floor(diff * 100 / compareCents);
  }

  // ----------------------------------------------------------------------
  // Markup builder
  // Mirrors snippets/promo-now.liquid output exactly. Used by both the
  // PDP patch and (via window.PromoNow.buildMarkup) the cart-recs slider.
  // ----------------------------------------------------------------------

  /**
   * @typedef {Object} PromoLocales
   * @property {string} label - "NOW"
   * @property {string} percentTemplate - "__PERCENT__% off"
   * @property {string} originalPrice - "Original price"
   * @property {string} salePrice - "Sale price"
   */

  /**
   * Build the .promo-now block as an HTMLDivElement.
   * @param {Object} variant - Shopify variant (price/compare in cents)
   * @param {string} moneyFormat
   * @param {string} context - 'pdp' | 'card' | 'cart'
   * @param {PromoLocales} locales
   * @returns {HTMLDivElement}
   */
  function buildMarkup(variant, moneyFormat, context, locales) {
    var ctx = context || 'card';
    var percent = calcPercentOff(variant.price, variant.compare_at_price);
    var percentText = locales.percentTemplate.replace(PERCENT_SENTINEL, percent);

    var root = document.createElement('div');
    root.className = 'promo-now promo-now--' + ctx;
    root.setAttribute('data-promo-now', 'true');
    root.setAttribute('data-promo-now-context', ctx);

    var compare = document.createElement('span');
    compare.className = 'promo-now__compare';
    var compareSr = document.createElement('span');
    compareSr.className = 'visually-hidden';
    compareSr.textContent = locales.originalPrice;
    compare.appendChild(compareSr);
    compare.appendChild(document.createTextNode(formatMoney(variant.compare_at_price, moneyFormat)));

    var price = document.createElement('span');
    price.className = 'promo-now__price';

    var label = document.createElement('span');
    label.className = 'promo-now__label';
    label.setAttribute('aria-hidden', 'true');
    label.textContent = locales.label;
    price.appendChild(label);

    var current = document.createElement('span');
    current.className = 'promo-now__current';
    var currentSr = document.createElement('span');
    currentSr.className = 'visually-hidden';
    currentSr.textContent = locales.salePrice;
    current.appendChild(currentSr);
    current.appendChild(document.createTextNode(formatMoney(variant.price, moneyFormat)));
    price.appendChild(current);

    var pill = document.createElement('span');
    pill.className = 'promo-now__pill';
    pill.textContent = percentText;

    root.appendChild(compare);
    root.appendChild(price);
    root.appendChild(pill);

    return root;
  }

  // ----------------------------------------------------------------------
  // PDP context extraction
  // Reads everything we need from the <product-price-element> data
  // attributes that product-price.liquid (file 7) will populate.
  // ----------------------------------------------------------------------

  /**
   * @typedef {Object} HostContext
   * @property {string[]} tags
   * @property {string} productType
   * @property {string} moneyFormat
   * @property {PromoLocales} locales
   */

  /**
   * @param {HTMLElement} host - The <product-price-element>
   * @returns {HostContext|null} null if required data is missing
   */
  function readHostContext(host) {
    var tagsAttr = host.getAttribute('data-product-tags') || '';
    var typeAttr = host.getAttribute('data-product-type') || '';
    var moneyFormat = host.getAttribute('data-money-format') || '';

    var locales = {
      label: host.getAttribute('data-promo-now-label') || 'NOW',
      percentTemplate: host.getAttribute('data-promo-now-percent-template') || (PERCENT_SENTINEL + '% off'),
      originalPrice: host.getAttribute('data-promo-now-original-price') || 'Original price',
      salePrice: host.getAttribute('data-promo-now-sale-price') || 'Sale price'
    };

    return {
      tags: parseTags(tagsAttr),
      productType: typeAttr,
      moneyFormat: moneyFormat,
      locales: locales
    };
  }

  // ----------------------------------------------------------------------
  // PDP patched updatePrices
  // ----------------------------------------------------------------------

  /**
   * Render or update the .promo-now block inside the host. If one already
   * exists, replace it; otherwise insert it as the first child.
   * @param {HTMLElement} host
   * @param {Object} variant
   * @param {HostContext} ctx
   */
  function applyPromoMarkup(host, variant, ctx) {
    var existing = host.querySelector(':scope > .promo-now');
    var fresh = buildMarkup(variant, ctx.moneyFormat, 'pdp', ctx.locales);
    if (existing) {
      existing.replaceWith(fresh);
    } else {
      host.insertBefore(fresh, host.firstChild);
    }
    host.classList.add(HOST_CLASS);
  }

  /**
   * Remove the .promo-now block from host (if any) and let native markup show.
   * @param {HTMLElement} host
   */
  function clearPromoMarkup(host) {
    var existing = host.querySelector(':scope > .promo-now');
    if (existing) existing.remove();
    host.classList.remove(HOST_CLASS);
  }

  /**
   * Patch the registered <product-price-element> class so that variant
   * changes route through our promo logic when applicable.
   * @param {Function} ProductPrice - The custom element class
   */
  function patchClass(ProductPrice) {
    var origUpdatePrices = ProductPrice.prototype.updatePrices;
    if (typeof origUpdatePrices !== 'function') {
      console.warn('[promo-now] product-price-element has no updatePrices method; skipping patch');
      return;
    }

    // Idempotency guard: if patched once, don't patch again. Useful in
    // dev scenarios where the script might run multiple times.
    if (ProductPrice.prototype.__promoNowPatched) return;
    ProductPrice.prototype.__promoNowPatched = true;

    ProductPrice.prototype.updatePrices = function(variant) {
      var ctx = readHostContext(this);

      // Non-promo product: delegate unchanged.
      if (!ctx || ctx.tags.indexOf(PROMO_TAG) === -1) {
        return origUpdatePrices.call(this, variant);
      }

      // Promo product but variant ineligible (sold out, no compareAt, etc.):
      // clear our markup, run native handler so price spans show correctly.
      if (!isEligible(ctx, variant)) {
        clearPromoMarkup(this);
        return origUpdatePrices.call(this, variant);
      }

      // Promo product, eligible variant: render/update promo markup.
      // We still call the native handler so .product-price--original /
      // .product-price--compare / .product-price--unit-* stay updated for
      // when CSS later toggles back. This keeps native state correct
      // without any visible side effects (CSS hides native while host has
      // .has-promo-now class).
      applyPromoMarkup(this, variant, ctx);
      return origUpdatePrices.call(this, variant);
    };
  }

  // ----------------------------------------------------------------------
  // Boot: wait for the custom element to register, then patch
  // ----------------------------------------------------------------------

  function boot(attempt) {
    var ProductPrice = customElements.get(TARGET_ELEMENT);
    if (ProductPrice) {
      patchClass(ProductPrice);
      return;
    }
    if (attempt >= RETRY_MAX_ATTEMPTS) {
      console.warn('[promo-now] ' + TARGET_ELEMENT + ' did not register within ' +
        (RETRY_INTERVAL_MS * RETRY_MAX_ATTEMPTS / 1000) + 's; giving up');
      return;
    }
    setTimeout(function() { boot(attempt + 1); }, RETRY_INTERVAL_MS);
  }

  // Prefer customElements.whenDefined: resolves the moment the class is
  // registered, no polling needed. Fall back to setTimeout retry on any
  // error path so we don't depend on whenDefined availability.
  if (typeof customElements !== 'undefined' && customElements.whenDefined) {
    customElements.whenDefined(TARGET_ELEMENT).then(function() {
      var ProductPrice = customElements.get(TARGET_ELEMENT);
      if (ProductPrice) patchClass(ProductPrice);
    }).catch(function() {
      boot(0);
    });
  } else {
    boot(0);
  }

  // ----------------------------------------------------------------------
  // Public API for cart-recommendations-slider.js (file 11)
  // ----------------------------------------------------------------------

  window.PromoNow = window.PromoNow || {};
  window.PromoNow.buildMarkup = buildMarkup;
  window.PromoNow.isEligible = isEligible;
  window.PromoNow.calcPercentOff = calcPercentOff;
  window.PromoNow.formatMoney = formatMoney;

})();